import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { useVenue } from "@/components/shared/VenueContext";
import { getMemToken } from "@/api/api";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Database, Download, Loader2, Play, ShieldAlert, Trash2, Cloud,
  CheckCircle2, AlertTriangle,
} from "lucide-react";
import moment from "moment";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

async function authFetch(path, options = {}) {
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(getMemToken() ? { Authorization: `Bearer ${getMemToken()}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "Error" }));
    throw new Error(err.detail || "Request failed");
  }
  return res.json();
}

export default function AdminBackups() {
  const { user, loading: venueLoading } = useVenue();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [pruning, setPruning] = useState(false);
  const [showPruneDialog, setShowPruneDialog] = useState(false);

  useEffect(() => {
    if (user) loadStatus();
  }, [user]);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const data = await authFetch("/api/admin/backup/status");
      setStatus(data);
    } catch (err) {
      toast.error(err.message);
      setStatus({ configured: false });
    }
    setLoading(false);
  };

  const handleRunBackup = async () => {
    setRunning(true);
    try {
      const data = await authFetch("/api/admin/backup/run", { method: "POST" });
      toast.success(`Backup creat: ${data.filename} (${data.size_mb} MB, ${data.summary?.total_documents || 0} documents)`);
      await loadStatus();
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    }
    setRunning(false);
  };

  const handlePrune = async () => {
    setPruning(true);
    setShowPruneDialog(false);
    try {
      const data = await authFetch("/api/admin/backup/prune", { method: "POST" });
      toast.success(`${data.deleted} backups antics eliminats. ${data.kept} conservats.`);
      await loadStatus();
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    }
    setPruning(false);
  };

  const handleDownload = async (filename) => {
    try {
      const data = await authFetch(`/api/admin/backup/download/${encodeURIComponent(filename)}`);
      window.open(data.download_url, "_blank");
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    }
  };

  if (venueLoading || loading) return <QuickLoader />;

  // Permission check
  if (!user || user.role !== "superadmin") {
    return (
      <div className="space-y-5">
        <PageHeader title="Backups" subtitle="Gestió de còpies de seguretat" />
        <Card className="p-8 text-center border-0 shadow-sm">
          <ShieldAlert className="w-12 h-12 mx-auto text-red-500 mb-3" />
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Accés denegat</h3>
          <p className="text-sm text-gray-600">Aquesta secció només és accessible per al Super Admin.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="admin-backups-page">
      <PageHeader
        title="Backups del sistema"
        subtitle="Còpies de seguretat de la base de dades a Backblaze B2"
        actions={
          status?.configured && (
            <div className="flex gap-2">
              <Button
                onClick={handleRunBackup}
                disabled={running}
                className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2 rounded-xl"
                data-testid="run-backup-btn"
              >
                {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                Crear backup ara
              </Button>
              <Button
                onClick={() => setShowPruneDialog(true)}
                disabled={pruning}
                variant="outline"
                className="gap-2 rounded-xl"
                data-testid="prune-backups-btn"
              >
                {pruning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Eliminar antics
              </Button>
            </div>
          )
        }
      />

      {!status?.configured ? (
        <Card className="p-6 border-0 shadow-sm bg-amber-50">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Backup no configurat</h3>
              <p className="text-sm text-gray-700 mb-2">
                Cal configurar les variables d'entorn de Backblaze B2 a Railway perquè els backups funcionin a producció:
              </p>
              <ul className="text-xs text-gray-600 space-y-0.5 ml-4 list-disc">
                <li><code className="bg-white px-1 rounded">B2_ENDPOINT_URL</code></li>
                <li><code className="bg-white px-1 rounded">B2_BUCKET_NAME</code></li>
                <li><code className="bg-white px-1 rounded">B2_KEY_ID</code></li>
                <li><code className="bg-white px-1 rounded">B2_APPLICATION_KEY</code></li>
                <li><code className="bg-white px-1 rounded">BACKUP_RETENTION_DAYS</code> (opcional, per defecte 30)</li>
              </ul>
            </div>
          </div>
        </Card>
      ) : (
        <>
          {/* Status overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Card className="p-4 border-0 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Estat</p>
                  <p className="font-semibold text-gray-900">Actiu</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 border-0 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                  <Cloud className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Bucket B2</p>
                  <p className="font-semibold text-gray-900 truncate">{status.bucket}</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 border-0 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                  <Database className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Backups guardats</p>
                  <p className="font-semibold text-gray-900">{status.count} (retenció: {status.retention_days}d)</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Schedule info */}
          <Card className="p-4 border-0 shadow-sm bg-gray-50">
            <p className="text-xs text-gray-600">
              ⏰ <strong>Backup automàtic:</strong> cada nit a les 02:00 UTC (~04:00 hora local).
              <strong className="ml-3">🗑️ Neteja automàtica:</strong> diàriament a les 03:00 UTC, eliminant backups de més de {status.retention_days} dies.
            </p>
          </Card>

          {/* Backups list */}
          {status.backups.length === 0 ? (
            <Card className="p-8 text-center border-dashed">
              <Database className="w-10 h-10 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-600 mb-1">Encara no hi ha cap backup</p>
              <p className="text-xs text-gray-500">Clica "Crear backup ara" per generar el primer.</p>
            </Card>
          ) : (
            <div className="space-y-2">
              {status.backups.map((b) => (
                <Card key={b.filename} className="p-4 border-0 shadow-sm" data-testid={`backup-${b.filename}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Database className="w-4 h-4 text-[#7B2D3F]" />
                        <span className="font-medium text-gray-900 truncate">{b.filename}</span>
                        <Badge variant="outline" className="text-xs">{b.size_mb} MB</Badge>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {moment(b.last_modified).format("DD/MM/YYYY HH:mm:ss")} · fa {moment(b.last_modified).fromNow()}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownload(b.filename)}
                      className="gap-1.5"
                      data-testid={`download-${b.filename}`}
                    >
                      <Download className="w-3.5 h-3.5" /> Descarregar
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      <AlertDialog open={showPruneDialog} onOpenChange={setShowPruneDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar backups antics?</AlertDialogTitle>
            <AlertDialogDescription>
              Es eliminaran tots els backups de més de <strong>{status?.retention_days} dies</strong>. Aquesta acció no es pot desfer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel·lar</AlertDialogCancel>
            <AlertDialogAction onClick={handlePrune} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
